import React, { useState, useEffect } from 'react';
import { DollarSign, Calendar, Clock, Package, Plus, Trash2, Edit3, MessageCircle, Share2, Link2, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ProfileEditModal from '../shared/ProfileModal';
import VideoCard from '../../UI/VideoCard';
import WorkDetailModal, { WorkDetail } from '../work/WorkDetailModal';
import SimpleUploadModal from '../shared/SimpleUploadModal';
import WorkSelectorModal from '../work/WorkSelectorModal';
import ExampleWorkSourceModal from '../shared/ExampleWorkSourceModal';
import ExampleWorkUrlModal from '../shared/ExampleWorkUrlModal';
import ExampleWorkEditModal from '../shared/ExampleWorkEditModal';
import ExampleWorkViewModal from '../shared/ExampleWorkViewModal';
import ExampleWorkUrlCard from '../shared/ExampleWorkUrlCard';
import { ALL_LEAF_CATEGORIES, STYLE_FACETS } from '../../../constants/tags';
import { SelectTags, type NewTagState } from '../../Tags/SelectTags';
import { deriveTagStateFromSource, flattenTagStateToNames, isKnownToolTag } from '../../Tags/tagStateUtils';
import { DatePicker } from '../../UI/DatePicker';
import SimpleMarkdownRenderer from '../../UI/SimpleMarkdownRenderer';
import { uploadMedia } from '../../../services/mediaUploadService';
import type { AIBriefGenerationResponse } from '../../../services/briefService';
import { useConfirm, useToast } from '../../UI/Toast';
import { pendingExampleWorksService, type PendingExampleWork } from '../../../services/pendingExampleWorksService';
import { postService, type WorkSummary } from '../../../services/postService';
import {
    buildBriefExampleWorkThreadId,
    mapWorkSummaryToExampleWork,
    normalizePersistedExampleWorkUrl,
    type ExampleWorkValue
} from '../shared/exampleWorkHelpers';
import { buildBriefSharePath, buildBriefShareText, buildBriefShareUrl, buildShareClipboardText, copyTextToClipboard } from '../../../utils/share';
import BriefAiWizardModal from './BriefAiWizardModal';
import type { BriefDetailType } from './types';
export type { BriefDetailType } from './types';

type ExampleWorkDraft = BriefDetailType['exampleWorks'][number] & {
    localFile?: File;
    localThumbnailDataUrl?: string;
};

type ExampleWorkPreview = ExampleWorkDraft & {
    commentThreadId?: string;
    comments?: WorkDetail['comments'];
    commentCount?: number;
};

type BriefFormData = Omit<BriefDetailType, 'exampleWorks'> & {
    exampleWorks: ExampleWorkDraft[];
};

interface BriefDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    brief?: BriefDetailType; // Optional for create
    isOwnProfile?: boolean;
    onSave?: (data: any) => void;
    initialIsEditing?: boolean;
    userInfo?: { id: string; name: string; avatar: string };
}


const getEmptyBrief = (): BriefDetailType => ({
    id: '',
    title: '',
    description: '',
    deliverables: '',
    budgetRange: '',
    deliveryTimeRange: '',
    communicationCount: 0,
    trialCount: 0,
    viewCount: 0,
    status: 'draft',
    tags: [],
    exampleWorks: [],
    targetDate: ''
});

const getDisplayValue = (value: string | undefined, defaultValue: string = '可谈') => {
    if (!value || value.trim() === '') return defaultValue;
    // 将旧的默认文案也转换为"可谈"
    const oldDefaults = ['未提供预算', '未提供工期', '未指定', '待定'];
    if (oldDefaults.includes(value.trim())) return defaultValue;
    return value;
};

const BriefDetailModal: React.FC<BriefDetailModalProps> = ({
    isOpen,
    onClose,
    brief: initialBrief,
    isOwnProfile = false,
    onSave,
    initialIsEditing = false,
    userInfo
}) => {

    const [brief, setBrief] = useState<BriefDetailType>(initialBrief || getEmptyBrief());
    const [isEditing, setIsEditing] = useState(initialIsEditing);

    // Form Data (separate from display data until saved, or sync?) 
    // Usually standard to have formData. For "Edit Toggle" we initialize formData from brief.
    const [formData, setFormData] = useState<BriefFormData>(brief as BriefFormData);

    // NEW Phase 1: Layered tag state
    const [tagState, setTagState] = useState<NewTagState>({
        primaryCategory: null,
        secondaryCategories: [],
        toolTags: [],
        styleFacets: [],
    });

    const [submitMode, setSubmitMode] = useState<'draft' | 'publish' | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const isSubmitting = submitMode !== null;
    const [selectedWork, setSelectedWork] = useState<WorkDetail | null>(null);
    const [selectedUrlWork, setSelectedUrlWork] = useState<ExampleWorkPreview | null>(null);
    const [editingExampleWork, setEditingExampleWork] = useState<ExampleWorkDraft | null>(null);
    const [isShareCopied, setIsShareCopied] = useState(false);
    const [isResolvingShare, setIsResolvingShare] = useState(false);
    const [isResolvingChat, setIsResolvingChat] = useState(false);
    const navigate = useNavigate();

    const currentUser = userInfo || { id: '0', name: '', avatar: '' };
    const { confirm } = useConfirm();
    const { showToast } = useToast();

    // Sub-modals state
    const [showExampleWorkSourceModal, setShowExampleWorkSourceModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showWorkSelectorModal, setShowWorkSelectorModal] = useState(false);
    const [showUrlModal, setShowUrlModal] = useState(false);
    const [availableWorks, setAvailableWorks] = useState<WorkSummary[]>([]);
    const [isLoadingAvailableWorks, setIsLoadingAvailableWorks] = useState(false);
    const [showAIWizard, setShowAIWizard] = useState(false);

    const initializedRef = React.useRef(false);
    const activeBriefIdRef = React.useRef<string | number | undefined>(undefined);
    const isMountedRef = React.useRef(true);
    const hasUnsavedChangesRef = React.useRef(false);
    const trackedViewBriefIdRef = React.useRef<string | null>(null);

    useEffect(() => {
        hasUnsavedChangesRef.current = hasUnsavedChanges;
    }, [hasUnsavedChanges]);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!isOpen) {
            initializedRef.current = false;
            activeBriefIdRef.current = undefined;
            trackedViewBriefIdRef.current = null;
            return;
        }

        const incomingId = initialBrief?.id;
        const isSameBrief = initializedRef.current && incomingId === activeBriefIdRef.current;
        if (isSameBrief) return;

        const dataToUse = initialBrief ? { ...initialBrief } : getEmptyBrief();
        // Ensure tags is initialized if missing in old data
        if (!dataToUse.tags) dataToUse.tags = [];

        setBrief(dataToUse);
        setFormData(dataToUse as BriefFormData);
        setTagState(deriveTagStateFromSource(dataToUse));
        setIsEditing(initialIsEditing || !initialBrief); // If no brief provided, force edit/create mode
        setHasUnsavedChanges(false);
        initializedRef.current = true;
        activeBriefIdRef.current = incomingId;
    }, [isOpen, initialBrief, initialIsEditing]);

    useEffect(() => {
        if (!isOpen || !brief.id) {
            return;
        }

        const briefId = String(brief.id);
        if (trackedViewBriefIdRef.current === briefId) {
            return;
        }
        trackedViewBriefIdRef.current = briefId;

        void (async () => {
            try {
                const { briefService } = await import('../../../services/briefService');
                await briefService.incrementViewCount(briefId);
                if (!isMountedRef.current) {
                    return;
                }
                setBrief((prev) => (
                    String(prev.id) === briefId
                        ? { ...prev, viewCount: (prev.viewCount || 0) + 1 }
                        : prev
                ));
                setFormData((prev) => (
                    String(prev.id) === briefId
                        ? { ...prev, viewCount: (prev.viewCount || 0) + 1 }
                        : prev
                ));
                window.dispatchEvent(new CustomEvent('briefs_updated', {
                    detail: { briefId, type: 'view_incremented' }
                }));
            } catch (error) {
                console.error('Failed to increment brief view count:', error);
            }
        })();
    }, [brief.id, isOpen]);

    const [pendingExampleWorks, setPendingExampleWorks] = useState<PendingExampleWork[]>([]);

    useEffect(() => {
        const currentId = brief.id || initialBrief?.id;
        setPendingExampleWorks(pendingExampleWorksService.getPendingExampleWorks('brief', currentId));

        if (!isOpen || !currentId) {
            return;
        }

        const handlePendingExampleWorksChanged = (event: Event) => {
            const detail = (event as CustomEvent<{ entityType: 'service' | 'brief'; entityId: string }>).detail;
            if (!detail || detail.entityType !== 'brief' || detail.entityId !== String(currentId)) {
                return;
            }

            setPendingExampleWorks(pendingExampleWorksService.getPendingExampleWorks('brief', currentId));
        };

        window.addEventListener(pendingExampleWorksService.eventName, handlePendingExampleWorksChanged);
        return () => window.removeEventListener(pendingExampleWorksService.eventName, handlePendingExampleWorksChanged);
    }, [brief.id, initialBrief?.id, isOpen]);
    const briefDisplayTags = flattenTagStateToNames(deriveTagStateFromSource(brief));

    const setFormField = <K extends keyof BriefFormData>(key: K, value: BriefFormData[K]) => {
        setFormData(prev => ({ ...prev, [key]: value }));
        setHasUnsavedChanges(true);
    };

    const isLocalUrl = (url?: string) => !!url && (url.startsWith('blob:') || url.startsWith('data:'));

    const stripLocalFields = (work: ExampleWorkDraft): BriefDetailType['exampleWorks'][number] => {
        const { localFile, localThumbnailDataUrl, ...rest } = work;
        return rest;
    };

    const normalizePersistedUrl = (url?: string) => {
        return normalizePersistedExampleWorkUrl(url);
    };

    const uploadExampleWorks = async (works: ExampleWorkDraft[]) => {
        const uploaded: BriefDetailType['exampleWorks'] = [];

        for (const work of works) {
            if (work.type === 'image') {
                let fileUrl = work.thumbnailUrl;
                let thumbnailUrl = work.thumbnailUrl;
                if (work.localFile) {
                    const uploadResult = await uploadMedia(work.localFile, undefined, () => undefined, {
                        altText: work.title
                    });
                    if (!uploadResult?.fileUrl) {
                        throw new Error('示例作品上传失败');
                    }
                    fileUrl = uploadResult.fileUrl;
                    thumbnailUrl = uploadResult.thumbnailUrl || uploadResult.fileUrl;
                }

                const resolvedUrl = normalizePersistedUrl(fileUrl || work.thumbnailUrl);
                const resolvedThumbnailUrl = normalizePersistedUrl(thumbnailUrl || resolvedUrl);
                const normalizedImages = work.images && work.images.length > 0
                    ? work.images.map((img) => {
                        if (isLocalUrl(img) && resolvedUrl && !isLocalUrl(resolvedUrl)) {
                            return resolvedUrl;
                        }
                        return normalizePersistedUrl(img);
                    })
                    : (resolvedUrl && !isLocalUrl(resolvedUrl) ? [resolvedUrl] : (work.images || []).map(normalizePersistedUrl));

                uploaded.push({
                    ...stripLocalFields(work),
                    thumbnailUrl: resolvedThumbnailUrl,
                    images: normalizedImages.filter((img) => img !== '')
                });
                continue;
            }

            let videoUrl = work.videoUrl;
            let thumbUrl = work.thumbnailUrl;
            if (work.localFile) {
                const uploadResult = await uploadMedia(work.localFile, undefined, () => undefined, {
                    altText: work.title
                });
                if (!uploadResult?.fileUrl) {
                    throw new Error('示例作品上传失败');
                }
                videoUrl = uploadResult.fileUrl;
                thumbUrl = uploadResult.thumbnailUrl || work.thumbnailUrl;
            }
            videoUrl = normalizePersistedUrl(videoUrl);
            thumbUrl = normalizePersistedUrl(thumbUrl);

            uploaded.push({
                ...stripLocalFields(work),
                videoUrl,
                thumbnailUrl: thumbUrl
            });
        }

        return uploaded;
    };

    const cloneExampleWorksSnapshot = (works: ExampleWorkDraft[]) => works.map((work) => ({
        ...work,
        images: work.images ? [...work.images] : undefined
    }));

    const getPersistedExampleWorks = (works: ExampleWorkDraft[]): BriefDetailType['exampleWorks'] => {
        const persistedWorks: BriefDetailType['exampleWorks'] = [];

        works.forEach((work) => {
            if (work.localFile) {
                return;
            }

            if (work.sourceUrl) {
                persistedWorks.push({
                    ...stripLocalFields(work),
                    thumbnailUrl: normalizePersistedUrl(work.thumbnailUrl) || '',
                    videoUrl: normalizePersistedUrl(work.videoUrl) || undefined,
                    images: (work.images || []).map(normalizePersistedUrl).filter(Boolean)
                });
                return;
            }

            if (work.type === 'image') {
                const normalizedImages = (work.images || [])
                    .map(normalizePersistedUrl)
                    .filter((url) => url && !isLocalUrl(url));
                const normalizedThumbnailUrl = normalizePersistedUrl(work.thumbnailUrl);
                const resolvedImages = normalizedImages.length > 0
                    ? normalizedImages
                    : (normalizedThumbnailUrl && !isLocalUrl(normalizedThumbnailUrl) ? [normalizedThumbnailUrl] : []);
                const resolvedThumbnailUrl = normalizedThumbnailUrl || resolvedImages[0] || '';

                if (!resolvedThumbnailUrl && resolvedImages.length === 0) {
                    return;
                }

                persistedWorks.push({
                    ...stripLocalFields(work),
                    thumbnailUrl: resolvedThumbnailUrl,
                    images: resolvedImages
                });
                return;
            }

            const normalizedVideoUrl = normalizePersistedUrl(work.videoUrl);
            const normalizedThumbnailUrl = normalizePersistedUrl(work.thumbnailUrl);
            if (!normalizedVideoUrl && !normalizedThumbnailUrl) {
                return;
            }

            persistedWorks.push({
                ...stripLocalFields(work),
                videoUrl: normalizedVideoUrl || undefined,
                thumbnailUrl: normalizedThumbnailUrl || ''
            });
        });

        return persistedWorks;
    };

    const getPendingExampleWorkPlaceholders = (works: ExampleWorkDraft[]): PendingExampleWork[] => {
        return works
            .filter((work) => work.localFile instanceof File)
            .map((work) => ({
                id: work.id,
                type: work.type,
                title: work.title,
                thumbnailUrl: work.localThumbnailDataUrl || work.thumbnailUrl,
                videoUrl: work.videoUrl,
                images: work.images,
                uploadState: 'uploading'
            }));
    };

    const syncExampleWorksInBackground = (briefId: string, worksSnapshot: ExampleWorkDraft[]) => {
        if (!worksSnapshot.some((work) => work.localFile instanceof File)) {
            return;
        }

        void (async () => {
            try {
                const uploadedWorks = await uploadExampleWorks(worksSnapshot);
                const { briefService } = await import('../../../services/briefService');
                const res = await briefService.updateBrief(briefId, { exampleWorks: uploadedWorks });

                if (!res.success || !res.data) {
                    throw new Error(res.error || '示例作品上传失败');
                }

                const nextBrief = ((res.data as any).data ?? res.data) as BriefDetailType;
                if (isMountedRef.current) {
                    setBrief(nextBrief);
                    if (!hasUnsavedChangesRef.current) {
                        setFormData((prev) => ({
                            ...prev,
                            id: nextBrief.id,
                            status: nextBrief.status,
                            exampleWorks: uploadedWorks as ExampleWorkDraft[]
                        }));
                    }
                }

                pendingExampleWorksService.clearPendingExampleWorks('brief', briefId);
                window.dispatchEvent(new CustomEvent('briefs_updated'));
                showToast('需求示例作品上传完成', 'success');
            } catch (error) {
                console.error('Background brief example works upload failed:', error);
                pendingExampleWorksService.clearPendingExampleWorks('brief', briefId);
                showToast('需求示例作品上传失败，请重新编辑后重试', 'error');
            }
        })();
    };

    const handleSave = async (mode: 'draft' | 'publish', e?: React.FormEvent, exitAfterSave = false) => {
        e?.preventDefault();
        setSubmitMode(mode);
        let saveSucceeded = false;
        let savedBrief: BriefDetailType | null = null;

        try {
            const alTags = flattenTagStateToNames(tagState);
            const exampleWorksSnapshot = cloneExampleWorksSnapshot(formData.exampleWorks);
            const hasPendingExampleUploads = exampleWorksSnapshot.some((work) => work.localFile instanceof File);
            const exampleWorks = getPersistedExampleWorks(exampleWorksSnapshot);
            const pendingExamplePlaceholders = getPendingExampleWorkPlaceholders(exampleWorksSnapshot);

            const nextStatus: BriefDetailType['status'] = mode === 'draft' ? 'draft' : 'active';
            const updatedData: BriefDetailType = {
                ...formData,
                tags: alTags,
                primaryCategory: tagState.primaryCategory,
                secondaryCategories: tagState.secondaryCategories,
                styleFacets: tagState.styleFacets,
                toolTags: tagState.toolTags,
                exampleWorks,
                status: nextStatus
            };

            // Dynamic import to avoid circular dependencies and ensure we get the latest service
            const { briefService } = await import('../../../services/briefService');

            if (isCreateMode) {
                // Remove id when creating as backend generates it
                const { id, ...createData } = updatedData as any;
                const res = await briefService.createBrief(createData);
                if (res.success && res.data) {
                    savedBrief = ((res.data as any).data ?? res.data) as BriefDetailType;
                    setBrief(savedBrief);
                    if (hasPendingExampleUploads && mode === 'draft' && !exitAfterSave) {
                        setFormData((prev) => ({
                            ...prev,
                            id: savedBrief!.id,
                            status: savedBrief!.status
                        }));
                    } else {
                        setFormData(savedBrief as BriefFormData);
                    }
                    window.dispatchEvent(new CustomEvent('briefs_updated'));
                    if (mode === 'publish') {
                        onSave?.(savedBrief);
                        if (isOwnProfile) {
                            window.dispatchEvent(new CustomEvent('change_profile_tab', { detail: { tab: 'briefs' } }));
                        }
                    }
                    if (hasPendingExampleUploads) {
                        pendingExampleWorksService.setPendingExampleWorks('brief', savedBrief.id, pendingExamplePlaceholders);
                        syncExampleWorksInBackground(String(savedBrief.id), exampleWorksSnapshot);
                    }
                    saveSucceeded = true;
                } else {
                    console.error('Failed to create brief', res.error);
                }
            } else {
                const res = await briefService.updateBrief(updatedData.id as string, updatedData);
                if (res.success && res.data) {
                    savedBrief = ((res.data as any).data ?? res.data) as BriefDetailType;
                    setBrief(savedBrief);
                    if (hasPendingExampleUploads && mode === 'draft' && !exitAfterSave) {
                        setFormData((prev) => ({
                            ...prev,
                            id: savedBrief!.id,
                            status: savedBrief!.status
                        }));
                    } else {
                        setFormData(savedBrief as BriefFormData);
                    }
                    window.dispatchEvent(new CustomEvent('briefs_updated'));
                    if (mode === 'publish') {
                        onSave?.(savedBrief);
                    }
                    if (hasPendingExampleUploads) {
                        pendingExampleWorksService.setPendingExampleWorks('brief', savedBrief.id, pendingExamplePlaceholders);
                        syncExampleWorksInBackground(String(savedBrief.id), exampleWorksSnapshot);
                    }
                    saveSucceeded = true;
                } else {
                    console.error('Failed to update brief', res.error);
                }
            }
        } catch (error) {
            console.error('Error saving brief:', error);
        } finally {
            setSubmitMode(null);

            if (saveSucceeded) {
                setHasUnsavedChanges(false);
                if (mode === 'publish') {
                    if (isCreateMode || brief.id === '' || brief.id === 0) {
                        onClose();
                    } else {
                        setIsEditing(false);
                    }
                } else if (exitAfterSave) {
                    if (savedBrief) {
                        setFormData(savedBrief as BriefFormData);
                    }
                    if (isCreateMode || !savedBrief) {
                        onClose();
                    } else {
                        setIsEditing(false);
                    }
                }
            }
        }
    };

    const closeEditor = () => {
        if (isCreateMode) {
            onClose();
            return;
        }

        setFormData(brief as BriefFormData);
        setTagState(deriveTagStateFromSource(brief));
        setIsEditing(false);
        setHasUnsavedChanges(false);
    };

    const handleRequestClose = async () => {
        if (isSubmitting) {
            return;
        }

        if (!isEditing) {
            onClose();
            return;
        }

        if (!hasUnsavedChanges) {
            closeEditor();
            return;
        }

        const result = await confirm({
            title: '保存草稿并退出？',
            message: '当前有未保存的修改，是否先保存为草稿再退出？',
            confirmText: '保存并退出',
            cancelText: '直接退出',
            dismissible: false,
            showCloseButton: true
        });

        if (result.action === 'confirm') {
            await handleSave('draft', undefined, true);
            return;
        }

        if (result.action === 'cancel') {
            closeEditor();
        }
    };

    const handleRemoveWork = (id: number | string) => {
        setFormData(prev => ({
            ...prev,
            exampleWorks: prev.exampleWorks.filter(w => w.id !== id)
        }));
        setHasUnsavedChanges(true);
    };

    const appendExampleWorks = (works: ExampleWorkValue[]) => {
        if (works.length === 0) {
            return;
        }

        setFormData((prev) => {
            const existingIds = new Set(prev.exampleWorks.map((work) => String(work.id)));
            const nextWorks = works.filter((work) => !existingIds.has(String(work.id)));

            if (nextWorks.length === 0) {
                return prev;
            }

            return {
                ...prev,
                exampleWorks: [...prev.exampleWorks, ...(nextWorks as ExampleWorkDraft[])]
            };
        });
        setHasUnsavedChanges(true);
    };

    const handlePublishWork = (data: { type: 'video' | 'image', file: File, previewUrl: string, thumbnailUrl: string, title?: string, description?: string }) => {
        const newWork: ExampleWorkDraft = {
            id: Date.now(),
            type: data.type,
            thumbnailUrl: data.thumbnailUrl,
            title: data.title || "未命名作品",
            description: data.description || '',
            videoUrl: data.type === 'video' ? data.previewUrl : undefined,
            images: data.type === 'image' ? [data.previewUrl] : [],
            localFile: data.file,
            localThumbnailDataUrl: data.type === 'video' ? data.thumbnailUrl : undefined
        };
        appendExampleWorks([newWork]);
    };

    const handleOpenCreatorWorks = async () => {
        if (!currentUser.id || currentUser.id === '0') {
            showToast('暂时无法获取你的作品列表', 'error');
            return;
        }

        setShowExampleWorkSourceModal(false);
        setAvailableWorks([]);
        setShowWorkSelectorModal(true);
        setIsLoadingAvailableWorks(true);
        try {
            const works = await postService.getCreatorPosts(currentUser.id, 100);
            setAvailableWorks(works.filter((work) => Boolean(work.thumbnailUrl || work.videoUrl || work.images?.length)));
        } catch (error) {
            console.error('Failed to load creator works for brief example selection:', error);
            showToast('加载作品列表失败，请稍后重试', 'error');
        } finally {
            setIsLoadingAvailableWorks(false);
        }
    };

    const handleSelectExampleWorkSource = (source: 'upload' | 'myWorks' | 'url') => {
        if (source === 'upload') {
            setShowExampleWorkSourceModal(false);
            setShowUploadModal(true);
            return;
        }

        if (source === 'myWorks') {
            void handleOpenCreatorWorks();
            return;
        }

        setShowExampleWorkSourceModal(false);
        setShowUrlModal(true);
    };

    const handleSelectCreatorWorks = (works: WorkSummary[]) => {
        appendExampleWorks(works.map(mapWorkSummaryToExampleWork));
    };

    const handleImportUrlWork = (work: ExampleWorkValue) => {
        appendExampleWorks([work]);
    };

    const handleStartEditExampleWork = (work: ExampleWorkDraft) => {
        setEditingExampleWork(work);
    };

    const handleSaveEditedExampleWork = (patch: Partial<ExampleWorkDraft> & { title: string; description?: string }) => {
        if (!editingExampleWork) {
            return;
        }

        setFormData((prev) => ({
            ...prev,
            exampleWorks: prev.exampleWorks.map((work) => (
                String(work.id) === String(editingExampleWork.id)
                    ? { ...work, ...patch }
                    : work
            ))
        }));
        setHasUnsavedChanges(true);
        setEditingExampleWork(null);
    };

    const handleWorkClick = (work: any) => {
        if (isEditing) return;
        const commentThreadId = brief.id
            ? buildBriefExampleWorkThreadId(brief.id, work.id)
            : undefined;
        if (work.sourceUrl) {
            setSelectedUrlWork({
                ...work,
                commentThreadId,
                ownerUserId: userInfo?.id || currentUser.id,
                comments: [],
                commentCount: 0
            });
            return;
        }

        const fullWork: WorkDetail = {
            id: work.id,
            type: work.type,
            title: work.title,
            thumbnailUrl: work.thumbnailUrl,
            videoUrl: work.videoUrl,
            images: work.images,
            description: work.description || "这是需求参考示例。",
            sourceUrl: work.sourceUrl,
            tags: [],
            likes: 0,
            views: 0,
            favorites: 0,
            comments: [],
            commentThreadId,
            createdAt: new Date().toISOString(),
            author: currentUser
        };
        setSelectedWork(fullWork);
    };

    const isCreateMode = !brief.id || brief.id === 0;
    const displayedExampleWorks = isEditing ? formData.exampleWorks : [...brief.exampleWorks, ...pendingExampleWorks];

    const resolveBriefOwner = async () => {
        let ownerId = userInfo?.id;
        let ownerName = userInfo?.name;
        let ownerAvatar = userInfo?.avatar;

        if (!ownerId) {
            const { briefService } = await import('../../../services/briefService');
            const response = await briefService.getBriefById(brief.id);
            const payload = response.data as any;
            const detail = payload?.data ?? payload;
            ownerId = detail?.client?.userId;
            ownerName = detail?.client?.displayName || detail?.client?.username || ownerId;
            ownerAvatar = detail?.client?.avatarUrl;
        }

        if (!ownerId) {
            throw new Error('暂时无法获取需求发布者信息');
        }

        return {
            ownerId,
            ownerName: ownerName || ownerId,
            ownerAvatar: ownerAvatar || ''
        };
    };

    // Header Actions
    const handleDelete = async () => {
        if (!brief.id) return;
        const result = await confirm({
            title: '删除需求',
            message: '确定要删除这个需求吗？此操作不可恢复。',
            confirmText: '删除',
            cancelText: '取消',
            tone: 'danger'
        });
        if (result.action !== 'confirm') return;

        try {
            const { briefService } = await import('../../../services/briefService');
            const res = await briefService.deleteBrief(brief.id as string);
            if (res.success) {
                window.dispatchEvent(new CustomEvent('briefs_updated'));
                onClose();
            } else {
                showToast(`删除失败：${res.error || '未知错误'}`, 'error');
            }
        } catch (error) {
            console.error('Delete brief error:', error);
            showToast('删除失败，请稍后重试', 'error');
        }
    };

    const headerActions = (
        <>
            {isEditing && isCreateMode && (
                <button
                    onClick={() => setShowAIWizard(true)}
                    className="px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2 transition-all bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-100 shadow-sm"
                >
                    <Sparkles className="w-4 h-4" />
                    AI 辅助生成
                </button>
            )}
            {!isEditing && !!brief.id && (
                <button
                    onClick={() => void (async () => {
                        if (isResolvingShare) return;

                        setIsResolvingShare(true);
                        try {
                            const { ownerId, ownerName } = await resolveBriefOwner();
                            const shareText = buildBriefShareText(brief.title, ownerName || '这位用户');
                            const shareUrl = buildBriefShareUrl(ownerId, brief.id);
                            await copyTextToClipboard(buildShareClipboardText(shareText, shareUrl));
                            setIsShareCopied(true);
                            window.setTimeout(() => setIsShareCopied(false), 1800);
                            showToast('分享信息已复制', 'success');
                        } catch (error: any) {
                            console.error('Prepare brief share failed:', error);
                            showToast(error?.message || '分享信息复制失败', 'error');
                        } finally {
                            setIsResolvingShare(false);
                        }
                    })()}
                    disabled={isResolvingShare}
                    className="p-2 rounded-full font-medium text-sm flex items-center justify-center transition-colors bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800 disabled:opacity-60"
                    title="分享需求"
                >
                    {isShareCopied ? <Link2 className="w-4 h-4 text-blue-600" /> : <Share2 className="w-4 h-4" />}
                </button>
            )}
            {!isEditing && isOwnProfile && (
                <>
                    <button
                        onClick={handleDelete}
                        className="p-2 rounded-full font-medium text-sm flex items-center justify-center transition-colors bg-red-50 text-red-500 hover:bg-red-100 hover:text-red-600"
                        title="删除需求"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => {
                            setIsEditing(true);
                            setHasUnsavedChanges(false);
                        }}
                        className="px-4 py-2 rounded-full font-medium text-sm flex items-center gap-2 transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                        <Edit3 className="w-4 h-4" />
                        编辑
                    </button>
                </>
            )}

            {!isEditing && !isOwnProfile && (
                <button
                    onClick={() => void (async () => {
                        if (isResolvingChat) return;

                        setIsResolvingChat(true);
                        try {
                            const { ownerId } = await resolveBriefOwner();
                            const convId = `C2C${ownerId}`;
                            navigate(`/messages/${convId}`, {
                                state: {
                                    returnTo: buildBriefSharePath(ownerId, brief.id),
                                    category: 'BRIEF',
                                    referenceId: brief.id.toString(),
                                    referenceTitle: brief.title,
                                    referenceDetails: {
                                        title: brief.title,
                                        description: brief.description,
                                        budgetRange: brief.budgetRange,
                                        tags: brief.tags,
                                        deadline: brief.targetDate,
                                    },
                                }
                            });
                        } catch (error: any) {
                            console.error('Create brief chat thread failed:', error);
                            showToast(error?.message || '暂时无法发起会话', 'error');
                        } finally {
                            setIsResolvingChat(false);
                        }
                    })()}
                    disabled={isResolvingChat}
                    className="px-4 py-2 rounded-full font-medium text-sm flex items-center gap-2 transition-colors bg-black text-white hover:bg-gray-800 disabled:opacity-60"
                >
                    <MessageCircle className="w-4 h-4" />
                    聊一聊
                </button>
            )}
        </>
    );

    return (
        <ProfileEditModal
            isOpen={isOpen}
            onClose={() => void handleRequestClose()}
            title={isEditing
                ? (isCreateMode ? "发布需求" : "编辑需求")
                : "需求详情"
            }
            description={isEditing
                ? (isCreateMode ? "描述您的创意需求，寻找最合适的创作者" : "修改需求详情和交付要求")
                : undefined
            }
            headerActions={headerActions}
            zIndex={60}
        >
            <form onSubmit={(e) => e.preventDefault()} className="space-y-8 pb-4">
                {/* Title & Description */}
                <div className={`rounded-2xl space-y-8 transition-all ${isEditing ? 'bg-white p-8 shadow-sm border border-gray-100' : ''}`}>
                    {/* Title Section */}
                    {isEditing ? (
                        <>
                            <div className="space-y-2">
                                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider">需求标题 <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={e => setFormField('title', e.target.value)}
                                    className="w-full text-3xl font-bold p-0 border-none focus:ring-0 placeholder-gray-300 text-gray-900 bg-transparent"
                                    placeholder="请输入需求标题"
                                    required
                                />
                                <div className="h-px w-full bg-gray-100"></div>
                            </div>

                            {/* Categorized Tags Selection */}
                            <div className="pt-2">
                                <SelectTags
                                    value={tagState}
                                    onChange={(next) => {
                                        setTagState(next);
                                        setHasUnsavedChanges(true);
                                    }}
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            {/* View Mode: Categorized Tags */}
                            <div className="flex items-center gap-3 mb-6">
                                <h2 className="text-3xl font-bold text-gray-900 leading-tight">{brief.title}</h2>
                                {brief.status === 'draft' && (
                                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                                        草稿
                                    </span>
                                )}
                            </div>
                        </>
                    )}

                    {/* Content Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-2">
                            {isEditing && <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider">需求详情描述</label>}

                            {isEditing ? (
                                <textarea
                                    value={formData.description}
                                    onChange={e => {
                                        setFormField('description', e.target.value);
                                        e.target.style.height = 'auto';
                                        e.target.style.height = e.target.scrollHeight + 'px';
                                    }}
                                    ref={(el) => {
                                        if (el) {
                                            el.style.height = 'auto';
                                            el.style.height = el.scrollHeight + 'px';
                                        }
                                    }}
                                    rows={4}
                                    className="w-full p-4 border-none bg-gray-50 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 text-base leading-relaxed text-gray-700 resize-vertical transition-all min-h-[160px]"
                                    placeholder="详细描述您的需求，包括风格、参考、具体要求等..."
                                />
                            ) : (
                                <div>
                                    <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                                        <div className="w-1 h-4 bg-black rounded-full"></div>
                                        需求详情
                                    </h4>
                                    <SimpleMarkdownRenderer
                                        content={brief.description}
                                        className="mb-6 text-base"
                                        emptyText="暂无需求描述"
                                    />

                                    {/* View Mode: Layered Tags */}
                                    {briefDisplayTags.length > 0 && (
                                        <div className="space-y-4 mb-6 pt-2 border-t border-gray-50">
                                            {/* Categories */}
                                            {(() => {
                                                const leafIds = ALL_LEAF_CATEGORIES.map(c => c.name);
                                                const catTags = briefDisplayTags.filter(t => leafIds.includes(t));
                                                if (catTags.length === 0) return null;
                                                return (
                                                    <div>
                                                        <h5 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-0.5">需求类目</h5>
                                                        <div className="flex flex-wrap gap-2">
                                                            {catTags.map((tag, i) => (
                                                                <span key={tag} className={`px-2 py-0.5 text-xs font-medium rounded border ${i === 0 ? 'bg-blue-600 text-white border-blue-600' : 'bg-blue-50 text-blue-600 border-blue-100'}`}>
                                                                    {i === 0 && <span className="opacity-70 mr-1">主</span>}
                                                                    {i > 0 && <span className="opacity-60 mr-1">次</span>}
                                                                    {tag}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                            {/* Style Facets */}
                                            {(() => {
                                                const facetNames = STYLE_FACETS.map(f => f.name) as string[];
                                                const styleTags = briefDisplayTags.filter(t => facetNames.includes(t));
                                                if (styleTags.length === 0) return null;
                                                return (
                                                    <div>
                                                        <h5 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-0.5">风格倾向</h5>
                                                        <div className="flex flex-wrap gap-2">
                                                            {styleTags.map(tag => (
                                                                <span key={tag} className="px-2 py-0.5 text-xs font-medium rounded border bg-blue-50 text-blue-600 border-blue-100">
                                                                    {tag}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                            {/* Tools */}
                                            {(() => {
                                                const leafIds = ALL_LEAF_CATEGORIES.map(c => c.name);
                                                const facetNames = STYLE_FACETS.map(f => f.name) as string[];
                                                const toolTags = briefDisplayTags.filter(t => !leafIds.includes(t) && !facetNames.includes(t));
                                                if (toolTags.length === 0) return null;
                                                return (
                                                    <div>
                                                        <h5 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-0.5">指定工具/模型与其他标签</h5>
                                                        <div className="flex flex-wrap gap-2">
                                                            {toolTags.map(tag => (
                                                                <span
                                                                    key={tag}
                                                                    className={`px-2 py-0.5 text-xs font-medium rounded border ${isKnownToolTag(tag) ? 'bg-purple-50 text-purple-600 border-purple-100' : 'bg-gray-100 text-gray-500 border-gray-200'}`}
                                                                >
                                                                    {tag}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="space-y-2">
                            {isEditing && <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider">需要交付的内容</label>}

                            {isEditing ? (
                                <textarea
                                    value={formData.deliverables}
                                    onChange={e => {
                                        setFormField('deliverables', e.target.value);
                                        e.target.style.height = 'auto';
                                        e.target.style.height = e.target.scrollHeight + 'px';
                                    }}
                                    ref={(el) => {
                                        if (el) {
                                            el.style.height = 'auto';
                                            el.style.height = el.scrollHeight + 'px';
                                        }
                                    }}
                                    rows={4}
                                    className="w-full p-4 border-none bg-blue-50/50 rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500/20 text-base leading-relaxed text-gray-700 resize-vertical transition-all min-h-[160px]"
                                    placeholder="例如：\n1. 15秒 1080p 视频文件\n2. 工程源文件\n3. ..."
                                />
                            ) : (
                                <div className="p-4 bg-blue-50/50 rounded-xl border border-blue-100 h-full">
                                    <h4 className="text-sm font-bold text-blue-900 mb-2 flex items-center gap-2">
                                        <Package className="w-4 h-4" />
                                        交付内容
                                    </h4>
                                    <SimpleMarkdownRenderer
                                        content={brief.deliverables}
                                        className="text-blue-800 [&_h3]:text-blue-900 [&_li]:text-blue-800 [&_p]:text-blue-800"
                                        emptyText="未指定交付内容"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Key Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className={`p-4 rounded-xl border shadow-sm flex items-center gap-3 transition-all ${isEditing ? 'bg-emerald-50/30 border-emerald-100' : 'bg-white border-gray-100 hover:shadow-md'}`}>
                        <div className={`p-2.5 rounded-full shrink-0 ${isEditing ? 'bg-emerald-100 text-emerald-600' : 'w-12 h-12 bg-emerald-100 text-emerald-600 flex items-center justify-center'}`}>
                            <DollarSign className={isEditing ? "w-5 h-5" : "w-6 h-6"} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-emerald-600 font-bold uppercase tracking-wide mb-1 opacity-80">预算区间</p>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={formData.budgetRange}
                                    onChange={e => setFormField('budgetRange', e.target.value)}
                                    className="w-full text-base font-bold text-gray-900 border-none bg-transparent p-0 focus:ring-0 placeholder-gray-400"
                                    placeholder="xx - xx (元)"
                                />
                            ) : (
                                <p className="text-xl font-bold text-gray-900">{getDisplayValue(brief.budgetRange)}</p>
                            )}
                        </div>
                    </div>

                    <div className={`p-4 rounded-xl border shadow-sm flex items-center gap-3 transition-all ${isEditing ? 'bg-orange-50/30 border-orange-100' : 'bg-white border-gray-100 hover:shadow-md'}`}>
                        <div className={`p-2.5 rounded-full shrink-0 ${isEditing ? 'bg-orange-100 text-orange-600' : 'w-12 h-12 bg-orange-100 text-orange-600 flex items-center justify-center'}`}>
                            <Calendar className={isEditing ? "w-5 h-5" : "w-6 h-6"} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-orange-600 font-bold uppercase tracking-wide mb-1 opacity-80">预计工期</p>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={formData.deliveryTimeRange}
                                    onChange={e => setFormField('deliveryTimeRange', e.target.value)}
                                    className="w-full text-base font-bold text-gray-900 border-none bg-transparent p-0 focus:ring-0 placeholder-gray-400"
                                    placeholder="xx - xx (天)"
                                />
                            ) : (
                                <p className="text-xl font-bold text-gray-900">{getDisplayValue(brief.deliveryTimeRange)}</p>
                            )}
                        </div>
                    </div>

                    <div className={`p-4 rounded-xl border shadow-sm flex items-center gap-3 transition-all ${isEditing ? 'bg-purple-50/30 border-purple-100' : 'bg-white border-gray-100 hover:shadow-md'}`}>
                        <div className={`p-2.5 rounded-full shrink-0 ${isEditing ? 'bg-purple-100 text-purple-600' : 'w-12 h-12 bg-purple-100 text-purple-600 flex items-center justify-center'}`}>
                            <Clock className={isEditing ? "w-5 h-5" : "w-6 h-6"} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-purple-600 font-bold uppercase tracking-wide mb-1 opacity-80">目标交付日期</p>
                            {isEditing ? (
                                <DatePicker
                                    value={formData.targetDate || ''}
                                    onChange={val => setFormField('targetDate', val)}
                                    className="w-full mt-1"
                                    placeholder="yyyy/mm/dd"
                                    position="right"
                                />
                            ) : (
                                <p className="text-xl font-bold text-gray-900">{getDisplayValue(brief.targetDate)}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Example Works Section */}
                <div className={`bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden ${!isEditing && 'hover:shadow-md transition-all'}`}>
                    <div className="px-8 py-6 border-b border-gray-100">
                        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            {isEditing ? "参考风格 / 示例" : "示例作品 / 参考风格"}
                            <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                {displayedExampleWorks.length}
                            </span>
                        </h3>
                    </div>

                    <div className="p-8">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {displayedExampleWorks.map(work => {
                                const sourceUrl = 'sourceUrl' in work ? work.sourceUrl : undefined;
                                const description = 'description' in work ? work.description : undefined;

                                return (
                                <div key={work.id} className="relative group">
                                    {sourceUrl ? (
                                        <ExampleWorkUrlCard
                                            type={work.type}
                                            thumbnailUrl={work.thumbnailUrl}
                                            videoUrl={work.videoUrl}
                                            images={work.images}
                                            title={work.title}
                                            description={description}
                                            sourceUrl={sourceUrl}
                                            onClick={() => handleWorkClick(work)}
                                        />
                                    ) : (
                                        <VideoCard
                                            id={work.id}
                                            type={work.type}
                                            images={work.images}
                                            videoUrl={work.videoUrl}
                                            thumbnailUrl={work.thumbnailUrl}
                                            title={work.title}
                                            likes={0}
                                            createdAt=""
                                            uploadState={!isEditing && 'uploadState' in work ? (work as PendingExampleWork).uploadState : undefined}
                                            onClick={() => handleWorkClick(work)}
                                        />
                                    )}
                                    {isEditing && (
                                        <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleStartEditExampleWork(work);
                                                }}
                                                className="p-1.5 bg-black/75 text-white rounded-full hover:bg-black transition-colors"
                                                title="编辑作品"
                                            >
                                                <Edit3 className="w-4 h-4" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleRemoveWork(work.id);
                                                }}
                                                className="p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                                                title="移除作品"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    )}
                                </div>
                                );
                            })}

                            {isEditing && (
                                <button
                                    type="button"
                                    className="h-full min-h-[200px] border-2 border-dashed border-gray-200 rounded-2xl flex flex-col items-center justify-center gap-2 text-gray-400 hover:text-gray-600 hover:border-gray-400 transition-all bg-gray-50 hover:bg-gray-100"
                                    onClick={() => setShowExampleWorkSourceModal(true)}
                                >
                                    <Plus className="w-8 h-8" />
                                    <span className="font-medium text-sm">
                                        {isLoadingAvailableWorks ? '加载作品中...' : '添加参考作品'}
                                    </span>
                                </button>
                            )}

                            {!isEditing && displayedExampleWorks.length === 0 && (
                                <div className="col-span-full py-12 text-center text-gray-400">
                                    暂无参考作品
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer Buttons (Only in Edit Mode) */}
                {
                    isEditing && (
                        <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
                            <button
                                type="button"
                                onClick={() => void handleRequestClose()}
                                className="px-6 py-2 rounded-full border border-gray-200 text-gray-600 font-medium hover:bg-gray-50 transition-colors"
                            >
                                取消
                            </button>
                            <button
                                type="button"
                                onClick={(e) => handleSave('draft', e)}
                                disabled={!formData.title || isSubmitting || !hasUnsavedChanges}
                                className={`px-8 py-2 rounded-full font-medium transition-all ${!formData.title || isSubmitting || !hasUnsavedChanges
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                            >
                                {submitMode === 'draft' ? '保存中...' : '保存'}
                            </button>
                            <button
                                type="button"
                                onClick={(e) => handleSave('publish', e)}
                                disabled={!formData.title || isSubmitting}
                                className={`px-8 py-2 rounded-full font-medium transition-all ${!formData.title || isSubmitting
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-black text-white hover:bg-gray-800'
                                    }`}
                            >
                                {submitMode === 'publish' ? '发布中...' : '发布'}
                            </button>
                        </div>
                    )
                }
            </form >

            <ExampleWorkSourceModal
                isOpen={showExampleWorkSourceModal}
                onClose={() => setShowExampleWorkSourceModal(false)}
                onSelect={handleSelectExampleWorkSource}
                canSelectMyWorks={!!currentUser.id && currentUser.id !== '0'}
                zIndex={70}
            />

            <SimpleUploadModal
                isOpen={showUploadModal}
                onClose={() => setShowUploadModal(false)}
                onSave={handlePublishWork}
                zIndex={70}
            />

            <WorkSelectorModal
                isOpen={showWorkSelectorModal}
                onClose={() => setShowWorkSelectorModal(false)}
                onSelect={handleSelectCreatorWorks}
                works={availableWorks}
                isLoading={isLoadingAvailableWorks}
                selectedWorkIds={formData.exampleWorks.map((work) => work.id)}
                zIndex={75}
            />

            <ExampleWorkUrlModal
                isOpen={showUrlModal}
                onClose={() => setShowUrlModal(false)}
                onImport={handleImportUrlWork}
                zIndex={80}
            />

            <ExampleWorkEditModal
                isOpen={!!editingExampleWork}
                work={editingExampleWork}
                onClose={() => setEditingExampleWork(null)}
                onSave={handleSaveEditedExampleWork}
                zIndex={85}
            />

            <ExampleWorkViewModal
                isOpen={!!selectedUrlWork}
                work={selectedUrlWork}
                onClose={() => setSelectedUrlWork(null)}
                zIndex={90}
            />

            {
                selectedWork && (
                    <WorkDetailModal
                        work={selectedWork}
                        isOpen={!!selectedWork}
                        onClose={() => setSelectedWork(null)}
                        isOwnProfile={isOwnProfile}
                        enablePostInteractions={false}
                    />
                )
            }

            <BriefAiWizardModal
                isOpen={showAIWizard}
                onClose={() => setShowAIWizard(false)}
                onComplete={(data: AIBriefGenerationResponse) => {
                    const derived = deriveTagStateFromSource({
                        tags: data.tags,
                        primaryCategory: data.primaryCategory,
                        secondaryCategories: data.secondaryCategories,
                        styleFacets: data.styleFacets,
                        toolTags: data.toolTags,
                    });

                    setTagState(derived);
                    setFormData(prev => ({
                        ...prev,
                        title: data.title,
                        description: data.description,
                        deliverables: data.deliverables,
                        tags: flattenTagStateToNames(derived),
                        primaryCategory: derived.primaryCategory,
                        secondaryCategories: derived.secondaryCategories,
                        styleFacets: derived.styleFacets,
                        toolTags: derived.toolTags,
                    }));
                    setHasUnsavedChanges(true);
                }}
            />
        </ProfileEditModal >
    );
};

export default BriefDetailModal;
